import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, Columns3, Download, Grid2X2, Image, List, Maximize, MoreHorizontal, Palette, Redo2, Rows3, Undo2, ZoomIn, ZoomOut } from "lucide-react";
import { PatternCanvas, type PatternCanvasHandle } from "../components/PatternCanvas";
import { IconButton, ProgressBar, Sheet } from "../components/ui";
import { useAppStore } from "../state/appStore";
import { completedCount, countByPalette, occupiedCount, selectionLabel } from "../core/pattern";
import { downloadBlob, exportProjectBundle, projectToCsv, projectToPng, projectToSvg } from "../core/exporters";
import { projectRepository } from "../core/database";
import type { WorkMode } from "../core/types";

const MODES: { value: WorkMode; label: string; icon: typeof Rows3 }[] = [
  { value: "row", label: "行", icon: Rows3 },
  { value: "column", label: "列", icon: Columns3 },
  { value: "block5", label: "5×5", icon: Grid2X2 },
  { value: "color", label: "颜色", icon: Palette }
];

export function EditorScreen() {
  const { activeProject: project, setScreen, setMode, moveSelection, completeSelection, toggleCell, setCellPalette, undo, redo, undoStack, redoStack, setToast, saveActive } = useAppStore();
  const canvas = useRef<PatternCanvasHandle>(null);
  const [showMore, setShowMore] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const counts = useMemo(() => project ? countByPalette(project) : new Map(), [project]);
  const total = project ? occupiedCount(project) : 0;
  const done = project ? completedCount(project) : 0;

  useEffect(() => {
    if (!project || !total || done / total < .2 || project.lastBackupAt) return;
    const reminderKey = `pindou:backup-reminder:${project.id}`;
    if (localStorage.getItem(reminderKey)) return;
    localStorage.setItem(reminderKey, "1");
    setToast("进度已超过 20%，建议导出 .pindou 备份");
  }, [done, project, setToast, total]);

  if (!project) return null;
  const currentProject = project;

  async function shareOrDownload(blob: Blob, filename: string) {
    const file = new File([blob], filename, { type: blob.type });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: filename }); return; } catch { /* user cancelled or fallback */ }
    }
    downloadBlob(blob, filename);
  }

  async function backup() {
    await saveActive();
    const images = await projectRepository.getImages(currentProject.sourceImageId);
    const backedUpProject = { ...currentProject, lastBackupAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    const blob = await exportProjectBundle(backedUpProject, images?.source, images?.preview);
    await shareOrDownload(blob, `${currentProject.name.replace(/[\\/:*?"<>|]/g, "-")}.pindou`);
    await projectRepository.update(backedUpProject);
    useAppStore.setState({ activeProject: backedUpProject });
    setToast("项目备份已生成");
  }

  return <main className="screen editor-screen">
    <header className="editor-header">
      <IconButton label="返回项目列表" onClick={() => { void saveActive(); setScreen("home"); }}><ArrowLeft /></IconButton>
      <div><h1>拼豆助手</h1><span>{project.width} × {project.height} · {project.palette.length} 色</span></div>
      <IconButton label="撤销" onClick={undo} disabled={!undoStack.length}><Undo2 /></IconButton>
      <IconButton label="更多" onClick={() => setShowMore(true)}><MoreHorizontal /></IconButton>
    </header>
    <div className="editor-progress"><span>已完成 <b>{done}</b> / {total}</span><ProgressBar value={done} max={total} /><IconButton label="适合屏幕" onClick={() => canvas.current?.fit()}><Maximize /></IconButton></div>
    <div className="region-toolbar"><strong>{selectionLabel(project)}</strong><div><IconButton label="缩小" onClick={() => canvas.current?.zoomBy(.82)}><ZoomOut /></IconButton><IconButton label="放大" onClick={() => canvas.current?.zoomBy(1.22)}><ZoomIn /></IconButton></div></div>
    <PatternCanvas ref={canvas} project={project} onCellTap={toggleCell} onCellLongPress={setEditIndex} />
    <nav className="mode-bar" aria-label="制作模式">{MODES.map(({ value, label, icon: ModeIcon }) => <button key={value} className={project.currentMode === value ? "active" : ""} onClick={() => setMode(value)}><ModeIcon />{label}</button>)}</nav>
    <footer className="editor-actions"><button className="secondary" onClick={() => moveSelection(-1)}><ChevronLeft />上一区</button><button className="primary" onClick={completeSelection}>完成本区</button><button className="secondary" onClick={() => moveSelection(1)}>下一区<ChevronRight /></button></footer>

    {editIndex != null && <Sheet title={`第 ${Math.floor(editIndex / project.width) + 1} 行 · 第 ${editIndex % project.width + 1} 列`} onClose={() => setEditIndex(null)}>
      <p className="sheet-helper">选择正确色号，或设为空格。修改图纸不会改变其他格子的制作进度。</p>
      <div className="palette-picker"><button onClick={() => { setCellPalette(editIndex, 0); setEditIndex(null); }}><i className="empty-swatch" />空格</button>{project.palette.map((entry) => <button key={entry.id} onClick={() => { setCellPalette(editIndex, entry.id); setEditIndex(null); }}><i style={{ background: `rgb(${entry.displayRGB.join(",")})` }} />{entry.code}</button>)}</div>
    </Sheet>}

    {showMore && <Sheet title="图纸与导出" onClose={() => setShowMore(false)}>
      <div className="sheet-actions"><button onClick={() => void backup()}><Download />备份 .pindou 项目</button><button onClick={() => void shareOrDownload(projectToSvg(project), `${project.name}.svg`)}><Grid2X2 />导出 SVG 矢量图</button><button onClick={() => void projectToPng(project).then((blob) => shareOrDownload(blob, `${project.name}.png`))}><Image />导出 PNG 图纸</button><button onClick={() => void shareOrDownload(projectToCsv(project), `${project.name}-色号统计.csv`)}><List />导出 CSV 色号统计</button><button disabled={!redoStack.length} onClick={redo}><Redo2 />重做上一步</button></div>
      <h3 className="subheading">色号与进度</h3>
      <div className="legend-list">{project.palette.map((entry) => { const count = counts.get(entry.id) ?? { total: 0, completed: 0 }; return <div key={entry.id}><i style={{ background: `rgb(${entry.displayRGB.join(",")})` }} /><b>{entry.code}</b><span>{count.completed} / {count.total}</span></div>; })}</div>
    </Sheet>}
  </main>;
}
